import { store } from 'store/store';
import { selectIsSystemSession } from 'features/application/selectors';
import { emitTelemetryEvent, TelemetryLevel } from 'utils/sentry/telemetryEvent';
import { PublishingKind } from 'utils/webrtc/types';

export interface PublishEvent {
  event: string;
  message: string;
  level: TelemetryLevel;
  // Narrower than FeedType: nothing in this folder emits 'receiving', so a caller
  // that tries is a compile error, not a convention. Still carried under the shared
  // `feedType` attribute name so cross-root (ice.*) queries cohort on one vocabulary.
  feedType: PublishingKind;
  attributes?: Record<string, unknown>;
  visibility?: 'fleet' | 'internal';
  cap?: number;
}

export const emitPublishEvent = ({
  event,
  message,
  level,
  feedType,
  attributes = {},
  visibility,
  cap,
}: PublishEvent): void => {
  emitTelemetryEvent({
    event,
    message,
    level,
    channel: 'streaming',
    visibility,
    cap,
    attributes: {
      ...attributes,
      feedType,
      isSystemSession: selectIsSystemSession(store.getState()),
      msSincePageLoad: performance.now(),
    },
  });
};

export const PUBLISH_RECONCILE_STALLED_EVENT = 'publish.reconcile.stalled';
export const PUBLISH_RECONCILE_STALLED_MESSAGE = '[publish] video reconcile did not converge';

export type CaptureSupersededPhase = 'capture' | 'rederive' | 'replace';
export const PUBLISH_CAPTURE_SUPERSEDED_EVENT = 'publish.capture.superseded';
export const publishCaptureSupersededMessage = (phase: CaptureSupersededPhase): string =>
  `[publish] video capture superseded — ${phase}`;

export type PublishDeferredReason = 'no-handle' | 'pending-reconnect';
export const PUBLISH_DEFERRED_EVENT = 'publish.deferred';
export const publishDeferredMessage = (reason: PublishDeferredReason): string =>
  `[publish] publish deferred — ${reason}`;

export const PUBLISH_DEFERRED_RESOLVED_EVENT = 'publish.deferred.resolved';
export const publishDeferredResolvedMessage = (reason: PublishDeferredReason): string =>
  `[publish] deferred publish resolved — ${reason}`;

export const PUBLISH_DEFERRED_CANCELLED_EVENT = 'publish.deferred.cancelled';
export const publishDeferredCancelledMessage = (reason: PublishDeferredReason): string =>
  `[publish] deferred publish cancelled — ${reason}`;

export const PUBLISH_NEGOTIATION_RESET_SKIPPED_EVENT = 'publish.negotiation.reset-skipped';
export const publishNegotiationResetSkippedMessage = (signalingState: RTCSignalingState): string =>
  `[publish] negotiation reset skipped — ${signalingState}`;

export const PUBLISH_VB_FALLBACK_EVENT = 'publish.vb.fallback';
export const PUBLISH_VB_FALLBACK_MESSAGE =
  '[publish] virtual background failed, publishing raw camera';

export const PUBLISH_AUDIO_HOT_MIC_EVENT = 'publish.audio.hot-mic';
export const PUBLISH_AUDIO_HOT_MIC_MESSAGE = '[publish] audio flowing while muted in UI';

export type ScreenshareReshareBranch = 'a' | 'b' | 'c';
export const PUBLISH_SCREENSHARE_RESHARE_EVENT = 'publish.screenshare.reshare';
export const publishScreenshareReshareMessage = (branch: ScreenshareReshareBranch): string =>
  `[publish] screenshare reshare — ${branch}`;

export const PUBLISH_SCREENSHARE_STREAM_MISSING_EVENT = 'publish.screenshare.stream-missing';
export const PUBLISH_SCREENSHARE_STREAM_MISSING_MESSAGE = '[publish] screenshare stream missing';
